import React, { useState, useEffect } from 'react';
import { countries } from '../data/countries';

interface State {
  name: string;
  cities: string[];
}

interface LocationSelectorProps {
  selectedCountry: string;
  selectedState: string;
  selectedCity: string;
  onCountryChange: (country: string) => void;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
  className?: string;
}

export default function LocationSelector({
  selectedCountry,
  selectedState,
  selectedCity,
  onCountryChange,
  onStateChange,
  onCityChange,
  className = ''
}: LocationSelectorProps) {
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  // Update states when country changes
  useEffect(() => {
    if (selectedCountry) {
      const countryData = countries.find(c => c.name === selectedCountry);
      if (countryData) {
        setStates(countryData.states);
        // Don't reset state if it already exists for this country
        if (!countryData.states.some(s => s.name === selectedState)) {
          onStateChange('');
          setCities([]);
        } else {
          // If state exists, update cities
          const stateData = countryData.states.find(s => s.name === selectedState);
          if (stateData) {
            setCities(stateData.cities);
          }
        }
      } else {
        setStates([]);
        setCities([]);
      }
    } else {
      setStates([]);
      setCities([]);
    }
  }, [selectedCountry, onStateChange]);

  // Update cities when state changes
  useEffect(() => {
    if (selectedCountry && selectedState) {
      const countryData = countries.find(c => c.name === selectedCountry);
      if (countryData) {
        const stateData = countryData.states.find(s => s.name === selectedState);
        if (stateData) {
          setCities(stateData.cities);
          // Don't reset city if it already exists for this state
          if (!stateData.cities.includes(selectedCity)) {
            onCityChange('');
          }
        } else {
          setCities([]);
          onCityChange('');
        }
      }
    } else {
      setCities([]);
      onCityChange('');
    }
  }, [selectedCountry, selectedState]);

  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Country *
        </label>
        <select
          id="country"
          value={selectedCountry}
          onChange={(e) => onCountryChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          required
        >
          <option value="">Select Country</option>
          {countries.map((country) => (
            <option key={country.name} value={country.name}>
              {country.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          State/Province *
        </label>
        <select
          id="state"
          value={selectedState}
          onChange={(e) => onStateChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          disabled={!selectedCountry}
          required
        >
          <option value="">Select State/Province</option>
          {states.map((state) => (
            <option key={state.name} value={state.name}>
              {state.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          City *
        </label>
        <select
          id="city"
          value={selectedCity}
          onChange={(e) => onCityChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          disabled={!selectedState}
          required
        >
          <option value="">Select City</option>
          {cities.map((city) => (
            <option key={city} value={city}>
              {city}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}